import { FCM_FOREGROUND_EVENT, parseFcmPayload, type FcmForegroundMessage } from "./fcmForegroundEvents";

const STORAGE_KEY = "seevi_notifications";
const MAX_STORED = 50;

export type StoredNotification = FcmForegroundMessage & {
  read: boolean;
  receivedAt: number;
};

type Listener = () => void;

let cache: StoredNotification[] | null = null;
const listeners = new Set<Listener>();

function load(): StoredNotification[] {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    cache = raw ? (JSON.parse(raw) as StoredNotification[]) : [];
  } catch {
    cache = [];
  }
  return cache!;
}

function persist(list: StoredNotification[]) {
  cache = list.slice(0, MAX_STORED);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    /* ignore quota errors */
  }
  listeners.forEach((fn) => fn());
}

export function subscribeNotifications(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getNotifications(): StoredNotification[] {
  return [...load()].sort((a, b) => b.receivedAt - a.receivedAt);
}

export function getUnreadCount(): number {
  return load().filter((n) => !n.read).length;
}

export function addNotification(message: FcmForegroundMessage): StoredNotification {
  const entry: StoredNotification = {
    ...message,
    read: false,
    receivedAt: Date.now(),
  };
  const next = [entry, ...load().filter((n) => n.id !== entry.id)].slice(0, MAX_STORED);
  persist(next);
  return entry;
}

export function markNotificationRead(id: string) {
  const next = load().map((n) => (n.id === id ? { ...n, read: true } : n));
  persist(next);
}

export function markAllNotificationsRead() {
  const next = load().map((n) => ({ ...n, read: true }));
  persist(next);
}

export function clearAllNotifications() {
  persist([]);
}

function onFcmMessage(event: Event) {
  const detail = (event as CustomEvent<FcmForegroundMessage>).detail;
  if (detail?.id) addNotification(detail);
}

let fcmListenerAttached = false;

export function initNotificationStore() {
  if (fcmListenerAttached) return;
  fcmListenerAttached = true;
  window.addEventListener(FCM_FOREGROUND_EVENT, onFcmMessage);

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === "seevi-fcm-background" && event.data.payload) {
        addNotification(parseFcmPayload(event.data.payload));
      }
    });
  }
}
